/**
 * Forms described in JSON Schema (dashboard-editor spec §5.6, F-1 to F-6): a driver's data source
 * (`sourceSchema`) and, later, a tile type's options. A form is fields, each one of:
 *
 * - listed by the driver (`"x-choices": true`), a fixed set (`enum`), fixed (`const`), typed
 *   (`string`, `number`, `integer`), or on and off (`boolean`);
 * - a sub-form (`object` with `properties`), to any depth;
 * - a list of sub-forms (`array` whose `items` is an object);
 * - a sub-form the driver gives at run time (`"x-form": "driver"`).
 *
 * An object may offer alternatives (`oneOf` or `anyOf`, each an object with a `title`): one of
 * several ways, each with its own fields, kept in the same object as the fields beside them.
 *
 * A field may wait for others (`"x-dependsOn": ["measurement"]`, or `"../name"` for a field of the
 * form around), and may be hidden unless others have given values (`"x-showWhen": { "func":
 * ["mean"] }`). A schema without any `x-dependsOn` keeps the rule from before: each listed field
 * waits for the listed fields before it.
 *
 * The functions here are what the editor needs and nothing it draws: what shows, what waits, what
 * a change clears, whether a form is complete.
 */

/** Where a field is in a form's value: names of objects, indices of list items. */
export type FormPath = readonly (string | number)[];

export type FormValue = Readonly<Record<string, unknown>>;

interface FieldBase {
    readonly name: string;
    readonly title: string;
    readonly description?: string;
    readonly isRequired: boolean;
    /** Fields that must have values first: names in the same object, or `../name` in the one around. */
    readonly dependsOn: readonly string[];
    /** Shown only while each named field has one of the values given. */
    readonly showWhen?: Readonly<Record<string, readonly unknown[]>>;
    /** What the field is when left out (`default`). */
    readonly defaultValue?: unknown;
    /** The field's schema as written, for what an editor reads beyond this (`x-hint`, a tile's `slot`). */
    readonly schema: Readonly<Record<string, unknown>>;
}

export type FormField =
    | (FieldBase & { readonly kind: 'choice'; readonly isListed: boolean; readonly values?: readonly string[]; readonly valueType: 'string' | 'number' })
    | (FieldBase & { readonly kind: 'text'; readonly valueType: 'string' | 'number' })
    | (FieldBase & { readonly kind: 'toggle' })
    | (FieldBase & { readonly kind: 'fixed'; readonly value: unknown })
    | (FieldBase & { readonly kind: 'group'; readonly form: Form })
    | (FieldBase & { readonly kind: 'list'; readonly item: Form; readonly itemTitle: string })
    | (FieldBase & { readonly kind: 'driverForm' });

export interface FormAlternative {
    readonly title: string;
    readonly description?: string;
    readonly form: Form;
}

export interface Form {
    /** The fields every way has, in order. */
    readonly fields: readonly FormField[];
    /** One of several ways, each with fields of its own (F-3). */
    readonly alternatives?: readonly FormAlternative[];
}

function isRecord(value: unknown) : value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const stringsOf = (value: unknown) : string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);

/** Whether a value counts as given: not missing, not empty. */
export function hasValue(value: unknown) : boolean {
    if (value === undefined || value === null || value === '') {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return true;
}

/** Whether any object in the schema declares `x-dependsOn` (otherwise the rule from before applies). */
function declaresDependencies(schema: unknown) : boolean {
    if (Array.isArray(schema)) {
        return schema.some(declaresDependencies);
    }
    if (!isRecord(schema)) {
        return false;
    }
    if (Array.isArray(schema['x-dependsOn'])) {
        return true;
    }

    return Object.values(schema).some(declaresDependencies);
}

const PLAIN_TYPES = new Set(['string', 'number', 'integer', 'boolean']);

/** The variants of a union, from `oneOf` or `anyOf`, leaving out `null` (zod's `.nullable()`). */
function variantsOf(schema: Record<string, unknown>) : Record<string, unknown>[] | undefined {
    const variants = Array.isArray(schema.oneOf) ? schema.oneOf : Array.isArray(schema.anyOf) ? schema.anyOf : undefined;

    return variants?.filter(isRecord).filter((variant) => variant.type !== 'null');
}

function readField(name: string, schema: Record<string, unknown>, isRequired: boolean, implicit: boolean, listedBefore: string[]) : FormField {
    // A union with a single variant (an optional made by zod) is that variant.
    const variants = variantsOf(schema);
    if (variants && variants.length === 1 && variants[0]) {
        return readField(name, { ...variants[0], ...withoutUnion(schema) }, isRequired, implicit, listedBefore);
    }

    const declared = stringsOf(schema['x-dependsOn']);
    const isListed = schema['x-choices'] === true;
    const base = {
        name,
        title: typeof schema.title === 'string' ? schema.title : name,
        ...(typeof schema.description === 'string' ? { description: schema.description } : {}),
        isRequired,
        dependsOn: implicit && isListed ? [...listedBefore] : declared,
        ...(isRecord(schema['x-showWhen']) ? { showWhen: Object.fromEntries(Object.entries(schema['x-showWhen']).map(([key, values]) => [key, Array.isArray(values) ? values : [values]])) } : {}),
        ...(schema.default !== undefined ? { defaultValue: schema.default } : {}),
        schema,
    };

    // A union of plain values (a text, a number or on and off) is typed as a text.
    if (variants && variants.length > 1 && variants.every((variant) => PLAIN_TYPES.has(String(variant.type)))) {
        return { ...base, kind: 'text', valueType: 'string' };
    }

    if (schema.const !== undefined) {
        return { ...base, kind: 'fixed', value: schema.const };
    }
    if (schema['x-form'] === 'driver') {
        return { ...base, kind: 'driverForm' };
    }
    if (schema.type === 'object' || isRecord(schema.properties) || (variants && variants.length > 1)) {
        return { ...base, kind: 'group', form: readObject(schema, implicit) };
    }
    if (schema.type === 'array' && isRecord(schema.items)) {
        return { ...base, kind: 'list', item: readObject(schema.items, implicit), itemTitle: typeof schema.items.title === 'string' ? schema.items.title : 'Item' };
    }
    if (schema.type === 'boolean') {
        return { ...base, kind: 'toggle' };
    }

    const valueType = schema.type === 'number' || schema.type === 'integer' ? 'number' : 'string';
    if (isListed || Array.isArray(schema.enum)) {
        return { ...base, kind: 'choice', isListed, ...(Array.isArray(schema.enum) ? { values: schema.enum.map(String) } : {}), valueType };
    }

    return { ...base, kind: 'text', valueType };
}

function withoutUnion(schema: Record<string, unknown>) : Record<string, unknown> {
    const { oneOf: _oneOf, anyOf: _anyOf, ...rest } = schema;

    return rest;
}

function readProperties(schema: Record<string, unknown>, implicit: boolean, listedBefore: string[]) : FormField[] {
    if (!isRecord(schema.properties)) {
        return [];
    }

    const required = new Set(stringsOf(schema.required));
    const fields: FormField[] = [];
    for (const [name, property] of Object.entries(schema.properties)) {
        if (!isRecord(property)) {
            continue;
        }

        const field = readField(name, property, required.has(name), implicit, listedBefore);
        fields.push(field);
        if (field.kind === 'choice' && field.isListed) {
            listedBefore.push(name);
        }
    }

    return fields;
}

function readObject(schema: Record<string, unknown>, implicit: boolean) : Form {
    const listedBefore: string[] = [];
    const variants = variantsOf(schema);
    if (!variants || variants.length < 2) {
        return { fields: readProperties(schema, implicit, listedBefore) };
    }

    const alternatives = variants.map((variant, index) => ({
        title: typeof variant.title === 'string' ? variant.title : `Way ${index + 1}`,
        ...(typeof variant.description === 'string' ? { description: variant.description } : {}),
        form: { fields: readProperties(variant, implicit, [...listedBefore]) },
    }));

    return { fields: readProperties(schema, implicit, listedBefore), alternatives };
}

/** A form from its JSON Schema (an object with `properties`, and perhaps alternatives). */
export function readForm(schema: unknown) : Form {
    if (!isRecord(schema)) {
        return { fields: [] };
    }

    return readObject(schema, !declaresDependencies(schema));
}

/** The names of an alternative's fields, and of theirs if it has alternatives of its own. */
function namesOf(form: Form) : string[] {
    return [...form.fields.map((field) => field.name), ...(form.alternatives ?? []).flatMap((alternative) => namesOf(alternative.form))];
}

/**
 * Which way of the form's alternatives is taken: the one asked for, else the first with a field
 * that has a value, else the first. Undefined for a form without alternatives.
 */
export function activeAlternative(form: Form, value: FormValue, preferred?: number) : number | undefined {
    const alternatives = form.alternatives;
    if (!alternatives || alternatives.length === 0) {
        return undefined;
    }
    if (preferred !== undefined && preferred >= 0 && preferred < alternatives.length) {
        return preferred;
    }

    const found = alternatives.findIndex((alternative) => alternative.form.fields.some((field) => field.kind !== 'fixed' && hasValue(value[field.name])));

    return found >= 0 ? found : 0;
}

/** The fields in play: the active way's first, then those every way has. */
export function fieldsInPlay(form: Form, value: FormValue, way?: number) : FormField[] {
    const active = activeAlternative(form, value, way);
    const alternative = active === undefined ? undefined : form.alternatives?.[active];

    return [...(alternative?.form.fields ?? []), ...form.fields];
}

export type FieldState =
    | { readonly status: 'shown' }
    | { readonly status: 'waiting'; readonly waitingFor: readonly string[] }
    | { readonly status: 'hidden' };

/** Looks a dependency up: `name` in this object, `../name` in the one around (`parents`, innermost last). */
function lookUp(reference: string, value: FormValue, parents: readonly FormValue[]) : unknown {
    let scope = value;
    let level = parents.length;
    let name = reference;
    while (name.startsWith('../')) {
        name = name.slice(3);
        level--;
        scope = parents[level] ?? {};
    }

    return scope[name];
}

/** What a field does now: shown, waiting for others (their titles), or hidden. */
export function fieldState(field: FormField, fields: readonly FormField[], value: FormValue, parents: readonly FormValue[] = []) : FieldState {
    if (field.kind === 'fixed') {
        return { status: 'hidden' };
    }
    for (const [name, values] of Object.entries(field.showWhen ?? {})) {
        if (!values.includes(lookUp(name, value, parents))) {
            return { status: 'hidden' };
        }
    }

    const missing = field.dependsOn.filter((name) => !hasValue(lookUp(name, value, parents)));
    if (missing.length > 0) {
        const titles = missing.map((name) => fields.find((other) => other.name === name)?.title ?? name.replace(/^(\.\.\/)+/, ''));

        return { status: 'waiting', waitingFor: titles };
    }

    return { status: 'shown' };
}

/** The values a field waits for, in the order of its `dependsOn`: when they change, its choices do. */
export function dependencyValues(field: FormField, value: FormValue, parents: readonly FormValue[] = []) : unknown[] {
    return field.dependsOn.map((name) => lookUp(name, value, parents));
}

/**
 * The fields that depend on `name`, and those that depend on them, in this object; not those
 * `isKept` keeps, nor, through them, what depends on them.
 */
function dependentsOf(fields: readonly FormField[], name: string, isKept: (field: FormField) => boolean = () => false) : Set<string> {
    const found = new Set<string>();
    const queue = [name];
    while (queue.length > 0) {
        const changed = queue.shift() as string;
        for (const field of fields) {
            if (!found.has(field.name) && (field.dependsOn.includes(changed) || Object.hasOwn(field.showWhen ?? {}, changed)) && !isKept(field)) {
                found.add(field.name);
                queue.push(field.name);
            }
        }
    }

    return found;
}

/** A sub-form's value with what depends on a changed field of the form around (`../name`) cleared. */
function clearFromAbove(form: Form, value: unknown, changed: string) : unknown {
    if (!isRecord(value)) {
        return value;
    }

    const fields = [...form.fields, ...(form.alternatives ?? []).flatMap((alternative) => alternative.form.fields)];
    const next: Record<string, unknown> = { ...value };
    for (const field of fields) {
        if (field.dependsOn.includes(`../${changed}`) || Object.hasOwn(field.showWhen ?? {}, `../${changed}`)) {
            for (const name of [field.name, ...dependentsOf(fields, field.name)]) {
                delete next[name];
            }
        }
    }

    return next;
}

/**
 * The form's value with one field set (or cleared, for `undefined`): the fields that depend on it
 * are cleared, here and in its sub-forms, and so are fields its new value hides. Others stay.
 * A listed choice that depends on it keeps its value while it has one (F-4): the form clears that
 * once the new choices come without it, so that a device's reading stays when another has it too.
 */
export function setField(form: Form, value: FormValue, name: string, fieldValue: unknown, way?: number) : Record<string, unknown> {
    const fields = fieldsInPlay(form, value, way);
    const next: Record<string, unknown> = { ...value };
    if (fieldValue === undefined) {
        delete next[name];
    } else {
        next[name] = fieldValue;
    }

    const isKept = (field: FormField) : boolean =>
        fieldValue !== undefined && field.kind === 'choice' && field.isListed && next[field.name] !== undefined && fieldState(field, fields, next).status === 'shown';

    for (const dependent of dependentsOf(fields, name, isKept)) {
        const field = fields.find((each) => each.name === dependent);
        // A field shown only for some values stays when the new value still shows it.
        if (field && field.dependsOn.length === 0 && fieldState(field, fields, next).status !== 'hidden') {
            continue;
        }
        delete next[dependent];
    }

    for (const field of fields) {
        if (field.kind === 'group' && next[field.name] !== undefined) {
            next[field.name] = clearFromAbove(field.form, next[field.name], name);
        }
        if (field.kind === 'list' && Array.isArray(next[field.name])) {
            next[field.name] = (next[field.name] as unknown[]).map((item) => clearFromAbove(field.item, item, name));
        }
    }

    return next;
}

/** The form's value after choosing a way: the fields of the other ways are cleared (F-3). */
export function chooseAlternative(form: Form, value: FormValue, way: number) : Record<string, unknown> {
    const keep = new Set(namesOf({ fields: [...form.fields, ...(form.alternatives?.[way]?.form.fields ?? [])] }));
    const next: Record<string, unknown> = {};
    for (const [name, fieldValue] of Object.entries(value)) {
        const belongsElsewhere = (form.alternatives ?? []).some((alternative, index) => index !== way && alternative.form.fields.some((field) => field.name === name));
        if (keep.has(name) || !belongsElsewhere) {
            next[name] = fieldValue;
        }
    }

    return next;
}

/** The fixed fields' values, which the form sets without asking. */
export function withFixedValues(form: Form, value: FormValue, way?: number) : Record<string, unknown> {
    const next: Record<string, unknown> = { ...value };
    for (const field of fieldsInPlay(form, value, way)) {
        if (field.kind === 'fixed') {
            next[field.name] = field.value;
        }
    }

    return next;
}

/**
 * Whether every required field in play that shows has a value, and every sub-form and list item
 * is complete in turn. A field the driver gives at run time counts once it has a value.
 */
export function isFormComplete(form: Form, value: FormValue, way?: number, parents: readonly FormValue[] = []) : boolean {
    const fields = fieldsInPlay(form, value, way);

    return fields.every((field) => {
        const state = fieldState(field, fields, value, parents);
        if (state.status === 'hidden') {
            return true;
        }

        const fieldValue = value[field.name];
        if (field.isRequired && !hasValue(fieldValue)) {
            return false;
        }
        if (field.kind === 'group' && isRecord(fieldValue)) {
            return isFormComplete(field.form, fieldValue, undefined, [...parents, value]);
        }
        if (field.kind === 'list' && Array.isArray(fieldValue)) {
            return fieldValue.every((item) => isRecord(item) && isFormComplete(field.item, item, undefined, [...parents, value]));
        }

        return true;
    });
}

/**
 * A sub-form's value in a few words, for the row that opens it: its values in order, switches
 * only when on. `describe` words a field its own way (a tile's slot: what its source reads), or
 * returns undefined for the usual way.
 */
export function summarize(form: Form, value: unknown, describe?: (field: FormField, value: unknown) => string | undefined) : string {
    if (!isRecord(value)) {
        return '';
    }

    const parts: string[] = [];
    for (const field of fieldsInPlay(form, value)) {
        const fieldValue = value[field.name];
        if (field.kind === 'fixed' || !hasValue(fieldValue)) {
            continue;
        }

        const own = describe?.(field, fieldValue);
        if (own !== undefined) {
            parts.push(own);
        } else if (field.kind === 'group') {
            parts.push(summarize(field.form, fieldValue, describe));
        } else if (field.kind === 'list') {
            parts.push(`${Array.isArray(fieldValue) ? fieldValue.length : 0} ${field.title.toLowerCase()}`);
        } else if (field.kind === 'toggle') {
            if (fieldValue === true) {
                parts.push(field.title);
            }
        } else if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
            parts.push(field.kind === 'choice' ? (choiceLabels(field)[String(fieldValue)] ?? String(fieldValue)) : String(fieldValue));
        }
    }

    return parts.filter((part) => part !== '').join(' · ');
}

/** The names a choice's values carry in its schema (`x-labels`), by value: "Next hours" for `short`. */
export function choiceLabels(field: FormField) : Readonly<Record<string, string>> {
    const labels = field.schema['x-labels'];

    return isRecord(labels) ? (labels as Record<string, string>) : {};
}

/** The value at a path in a nested value. */
export function valueAt(value: unknown, path: FormPath) : unknown {
    let current = value;
    for (const key of path) {
        if (typeof key === 'number') {
            current = Array.isArray(current) ? current[key] : undefined;
        } else {
            current = isRecord(current) ? current[key] : undefined;
        }
    }

    return current;
}
